/**
 * `@login` autocomplete shared by both editor modes. The popup is plain DOM
 * (appended to <body>, positioned at a caret rect) so it works inside TipTap's
 * suggestion plugin and next to a textarea alike. Picking inserts plain text
 * `@login ` — Markdown stays the source of truth and the notifier resolves the
 * same `@login` when the comment is saved.
 */
import { Extension } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";

export interface MentionUser {
  /** What gets inserted after `@` (the part of the login before `@`). */
  handle: string;
  name: string;
}

let provider: () => MentionUser[] = () => [];
/** The editor tells the extension where users come from (the client store's schema). */
export const setMentionUsers = (fn: () => MentionUser[]): void => {
  provider = fn;
};

export function matchUsers(query: string, limit = 8): MentionUser[] {
  const q = query.toLowerCase();
  const all = provider();
  const starts = all.filter((u) => u.handle.toLowerCase().startsWith(q) || u.name.toLowerCase().startsWith(q));
  const contains = all.filter((u) => !starts.includes(u) && (u.handle.toLowerCase().includes(q) || u.name.toLowerCase().includes(q)));
  return [...starts, ...contains].slice(0, limit);
}

export class MentionPopup {
  private el: HTMLDivElement | null = null;
  private items: MentionUser[] = [];
  private index = 0;
  private pick: (u: MentionUser) => void = () => {};

  get open(): boolean {
    return this.el !== null;
  }

  show(items: MentionUser[], rect: DOMRect | null | undefined, pick: (u: MentionUser) => void): void {
    this.items = items;
    this.pick = pick;
    if (items.length === 0) {
      this.hide();
      return;
    }
    if (!this.el) {
      this.el = document.createElement("div");
      this.el.className = "mention-popup";
      this.el.setAttribute("role", "listbox");
      document.body.appendChild(this.el);
    }
    this.index = Math.min(this.index, items.length - 1);
    if (rect) {
      const below = rect.bottom + 4;
      const flip = below + 240 > window.innerHeight;
      this.el.style.left = `${Math.min(rect.left, window.innerWidth - 260)}px`;
      this.el.style.top = flip ? "" : `${below}px`;
      this.el.style.bottom = flip ? `${window.innerHeight - rect.top + 4}px` : "";
    }
    this.render();
  }

  hide(): void {
    this.el?.remove();
    this.el = null;
    this.items = [];
    this.index = 0;
  }

  /** Arrow keys, Enter / Tab, Esc. Returns true when the key was consumed. */
  handleKey(e: KeyboardEvent): boolean {
    if (!this.el) return false;
    if (e.key === "ArrowDown") {
      this.index = (this.index + 1) % this.items.length;
      this.render();
      return true;
    }
    if (e.key === "ArrowUp") {
      this.index = (this.index - 1 + this.items.length) % this.items.length;
      this.render();
      return true;
    }
    if (e.key === "Enter" || e.key === "Tab") {
      const u = this.items[this.index];
      if (u) this.pick(u);
      return true;
    }
    if (e.key === "Escape") {
      this.hide();
      return true;
    }
    return false;
  }

  private render(): void {
    if (!this.el) return;
    this.el.replaceChildren(
      ...this.items.map((u, i) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = `mention-item ${i === this.index ? "active" : ""}`;
        b.setAttribute("role", "option");
        b.innerHTML = `<b>${escape(u.handle)}</b> <span>${escape(u.name)}</span>`;
        b.addEventListener("mousedown", (ev) => {
          ev.preventDefault();
          this.pick(u);
        });
        return b;
      }),
    );
  }
}

const escape = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** TipTap extension: `@` opens the popup; picking inserts `@handle ` as text. */
export const MentionSuggest = Extension.create({
  name: "mentionSuggest",
  addProseMirrorPlugins() {
    const popup = new MentionPopup();
    return [
      Suggestion<MentionUser>({
        editor: this.editor,
        char: "@",
        allowSpaces: false,
        items: ({ query }) => matchUsers(query),
        command: ({ editor, range, props }) => {
          editor.chain().focus().insertContentAt(range, `@${props.handle} `).run();
        },
        render: () => ({
          onStart: (p) => popup.show(p.items, p.clientRect?.(), (u) => p.command(u)),
          onUpdate: (p) => popup.show(p.items, p.clientRect?.(), (u) => p.command(u)),
          onKeyDown: ({ event }) => popup.handleKey(event),
          onExit: () => popup.hide(),
        }),
      }),
    ];
  },
});

/** Markdown mode: the `@word` being typed before the caret, if any. */
export function mentionAtCaret(ta: HTMLTextAreaElement): { start: number; query: string } | null {
  const pos = ta.selectionStart;
  if (pos !== ta.selectionEnd) return null;
  const before = ta.value.slice(Math.max(0, pos - 60), pos);
  const m = /(?:^|[\s(])@([\w.\-]*)$/.exec(before);
  if (!m) return null;
  return { start: pos - m[1]!.length - 1, query: m[1]! };
}
